import React, { useContext, useEffect, useMemo } from 'react';
import { MultipleSelect } from './MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import { AppContext, IAppContext } from 'appState/appContext';
import { IDishAssignmentContext, DishAssignmentContext } from '../../../dishAssignmentState/dishAssignmentContext';
import { UserRoles, IGetListRestaurantsForAssignDishes, IGetListRestaurantsForAssignDishesArguments } from 'types';
import { useFetchData } from 'hooks/useFetchData';
import { getListRestaurantsForAssignDishesV2 } from 'backend/dishes';
import { useRoleAccess } from 'hooks/useRoleAccess';
import style from './RestaurantMultipleSelect.module.scss';

export const RestaurantMultipleSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const {
		setAdditionalRestaurants,
		setFetchedRestaurants,
		IDSelectedCustomer,
		IDSelectedRestaurant,
		selectedAdditionalRestaurants,
		dishes,
		isApplyToOne,
		isVisibleAdditionalRestaurantsModal,
	} = useContext<IDishAssignmentContext>(DishAssignmentContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const listIDsOfSubcategoriesThatHaveBeenChanged = useMemo(() => {
		return dishes.filter((el) => el.isChanged).map((el) => el.id);
	}, [dishes]);

	const { response, loading, refetch, responseError } = useFetchData<
		IGetListRestaurantsForAssignDishes,
		IGetListRestaurantsForAssignDishesArguments
	>(
		IDSelectedCustomer && listIDsOfSubcategoriesThatHaveBeenChanged.length
			? getListRestaurantsForAssignDishesV2
			: null,
		{
			customerId: IDSelectedCustomer ? IDSelectedCustomer : '',
			subCategoryIds: listIDsOfSubcategoriesThatHaveBeenChanged,
			dishIds: dishes.flatMap((el) => el.dishes.map((el) => el.id)),
		},
	);

	const additionalRestaurants = useMemo(() => {
		const accessibleRestaurantIDs = loggedUser.restaurants ? loggedUser.restaurants.map((el) => el.id) : [];
		const restaurantsFromServer = response?.data.restaurants.filter((el) => el.id !== IDSelectedRestaurant) || [];
		const intersectedRestaurants = restaurantsFromServer.filter((el) => accessibleRestaurantIDs.includes(el.id));
		return hasAccess ? restaurantsFromServer : intersectedRestaurants;
	}, [response, IDSelectedRestaurant, loggedUser, hasAccess]);

	const handleRemove = (id: string) => {
		const selectedItems = selectedAdditionalRestaurants.filter((el) => el !== id);
		setAdditionalRestaurants(selectedItems);
	};

	useEffect(() => {
		if (response) {
			setFetchedRestaurants(response.data.restaurants);
		}
	}, [response]);

	const restaurantsList = useMemo(() => {
		return selectedAdditionalRestaurants.map((el) => {
			const label = additionalRestaurants.find((item) => item.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [handleRemove, selectedAdditionalRestaurants, additionalRestaurants]);

	return (
		<div className={style.container} data-test="RestaurantField">
			<div className={style.field}>
				<MultipleSelect
					data={additionalRestaurants}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					isApplyToOne={isApplyToOne}
					setAdditionalRestaurants={setAdditionalRestaurants}
					selectedAdditionalRestaurants={selectedAdditionalRestaurants}
					isVisibleAdditionalRestaurantsModal={isVisibleAdditionalRestaurantsModal}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};
